import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Activity, Heart, ShieldCheck } from "lucide-react";
import PhoneFrame from "../components/PhoneFrame.jsx";
import { Wordmark, Spine } from "../components/Brand.jsx";
import api from "../lib/api.js";
import { useAuth } from "../lib/auth.jsx";

const GOAL_OPTIONS = [
  "Improve Posture", "Walk More", "Reduce Back Strain", "Increase Energy",
  "Weight Management", "Reduce Stress", "Improve Mobility", "Improve Confidence", "Build Strength",
];
const BASELINE_KEYS = [
  { key: "posture", label: "Posture" },
  { key: "energy", label: "Energy" },
  { key: "stress", label: "Stress" },
  { key: "mood", label: "Mood" },
  { key: "back_comfort", label: "Back Comfort" },
  { key: "walking_endurance", label: "Walking Endurance" },
];
const HEALTH = ["Apple Health", "Google Health Connect", "Fitbit"];

export default function Onboarding() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [goals, setGoals] = useState([]);
  const [baseline, setBaseline] = useState(Object.fromEntries(BASELINE_KEYS.map((b) => [b.key, 5])));
  const [integrations, setIntegrations] = useState([]);
  const [employer, setEmployer] = useState("");
  const [loading, setLoading] = useState(false);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding", {
        goals, baseline, health_integrations: integrations, employer_code: employer || null,
      });
      await refreshUser();
      toast.success("Welcome to Posture Steps! +100 points");
      nav("/home");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: "Welcome",
      content: (
        <div className="space-y-7 text-center pt-10 ps-fade-up">
          <div className="mx-auto relative w-20 h-20 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-[#4ade80]/30 blur-2xl ps-pulse" />
            <div className="relative"><Spine size={72} /></div>
          </div>
          <div>
            <div className="ps-chip mx-auto mb-4">A new chapter</div>
            <h1 className="ps-heading text-[2.6rem] font-extrabold leading-[1.0] tracking-tight">
              Walk taller.<br />
              <span className="ps-serif ps-italic font-normal ps-gradient-text">Feel stronger.</span><br />
              Live better.
            </h1>
          </div>
          <p className="text-slate-300 max-w-[280px] mx-auto ps-serif ps-italic leading-relaxed">
            A measurable journey to better posture, more energy and lasting wellness.
          </p>
        </div>
      ),
    },
    {
      title: "Integrations",
      content: (
        <div className="pt-2">
          <h2 className="ps-heading text-2xl font-bold">Connect Health Data</h2>
          <p className="text-slate-400 mt-1 text-sm">We'll automatically count your steps. You can skip and enter manually.</p>
          <div className="mt-6 space-y-3">
            {HEALTH.map((h) => (
              <button
                key={h}
                data-testid={`integration-${h.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => toggle(integrations, setIntegrations, h)}
                className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all ${
                  integrations.includes(h)
                    ? "border-[#2fbf71] bg-[#142534]"
                    : "border-[#1a2f40] bg-[#0e1d2a]"
                }`}
              >
                <div className="flex items-center gap-3">
                  <Activity size={18} color="#2fbf71" />
                  <span className="font-medium">{h}</span>
                </div>
                {integrations.includes(h) ? <Check size={18} color="#2fbf71" /> : <span className="text-slate-500 text-sm">Connect</span>}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Employer",
      content: (
        <div className="pt-2">
          <h2 className="ps-heading text-2xl font-bold">Corporate Wellness</h2>
          <p className="text-slate-400 mt-1 text-sm">Participating through your employer? Enter your code.</p>
          <input
            data-testid="onboard-employer-code"
            placeholder="e.g. PERSONIFY-ACME"
            value={employer} onChange={(e) => setEmployer(e.target.value)}
            className="mt-4 w-full bg-[#142534] border border-[#1a2f40] rounded-2xl px-4 py-3.5 text-white outline-none focus:border-[#2fbf71]"
          />
          <div className="mt-3 ps-card p-4 flex gap-3">
            <ShieldCheck size={20} color="#2fbf71" />
            <p className="text-xs text-slate-400">Personify Health integrated. Only aggregate metrics are shared — never personal data.</p>
          </div>
        </div>
      ),
    },
    {
      title: "Goals",
      content: (
        <div className="pt-2">
          <h2 className="ps-heading text-2xl font-bold">Your Wellness Goals</h2>
          <p className="text-slate-400 mt-1 text-sm">Pick everything that matters to you.</p>
          <div className="mt-5 grid grid-cols-2 gap-3">
            {GOAL_OPTIONS.map((g) => (
              <button
                key={g}
                data-testid={`goal-${g.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => toggle(goals, setGoals, g)}
                className={`text-left p-4 rounded-2xl border text-sm font-medium ${
                  goals.includes(g)
                    ? "border-[#2fbf71] bg-[#142534] text-white"
                    : "border-[#1a2f40] bg-[#0e1d2a] text-slate-300"
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Baseline",
      content: (
        <div className="pt-2">
          <h2 className="ps-heading text-2xl font-bold">Baseline Assessment</h2>
          <p className="text-slate-400 mt-1 text-sm">Rate each from 1-10. We'll measure your progress from here.</p>
          <div className="mt-5 space-y-5">
            {BASELINE_KEYS.map((b) => (
              <div key={b.key}>
                <div className="flex justify-between text-sm font-medium">
                  <span>{b.label}</span>
                  <span className="text-[#2fbf71]">{baseline[b.key]}</span>
                </div>
                <input
                  data-testid={`baseline-${b.key}`}
                  type="range" min="1" max="10" value={baseline[b.key]}
                  onChange={(e) => setBaseline({ ...baseline, [b.key]: Number(e.target.value) })}
                  className="ps-slider mt-2"
                />
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Ready",
      content: (
        <div className="text-center pt-8 space-y-5 ps-fade-up">
          <div className="mx-auto relative w-24 h-24 flex items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-[#4ade80]/30 blur-2xl ps-pulse" />
            <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-[#4ade80]/40 to-[#22d3ee]/30 border border-[#4ade80]/30 flex items-center justify-center">
              <Heart size={36} color="#4ade80" />
            </div>
          </div>
          <h2 className="ps-heading text-3xl font-extrabold">
            You're <span className="ps-serif ps-italic font-normal ps-gradient-text">calibrated.</span>
          </h2>
          <p className="text-slate-300 px-4 ps-serif ps-italic leading-relaxed">
            Your baseline is set. Tap Begin to enter your dashboard and earn your first Posture Points™.
          </p>
        </div>
      ),
    },
  ];

  const last = step === steps.length - 1;

  return (
    <PhoneFrame>
      <div className="ps-aurora">
        <div className="px-6 pt-6 pb-32 min-h-screen flex flex-col relative">
          <Wordmark className="self-center" />
          <div className="mt-7 flex gap-1.5">
            {steps.map((s, i) => (
              <div key={s.key || i} className={`h-1 flex-1 rounded-full transition-all ${i <= step ? "bg-gradient-to-r from-[#4ade80] to-[#22d3ee]" : "bg-[#15303E]"}`} />
            ))}
          </div>
          <div className="flex-1">{steps[step].content}</div>
          <div className="mt-8 flex gap-3">
            {step > 0 && (
              <button
                data-testid="onboard-back"
                onClick={() => setStep(step - 1)}
                className="ps-button-secondary flex-1 inline-flex items-center justify-center gap-2"
              >
                <ArrowLeft size={16} /> Back
              </button>
            )}
            <button
              data-testid="onboard-next"
              disabled={loading}
              onClick={() => (last ? submit() : setStep(step + 1))}
              className="ps-button-primary flex-1 inline-flex items-center justify-center gap-2"
            >
              {last ? (loading ? "Saving…" : "Begin") : "Continue"} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </PhoneFrame>
  );
}
